import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { AppHeader } from "@/components/AppHeader";
import { ListingCard } from "@/components/ListingCard";
import { FilterBar, type Filters } from "@/components/FilterBar";
import { getListings } from "@/data/listings";

export default function Discover() {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Filters>({
    sizes: [],
    categories: [],
    maxPrice: 2000,
    maxDistance: 50,
  });

  const { data: listings = [], isLoading } = useQuery({
    queryKey: ["listings"],
    queryFn: getListings,
  });

  const filtered = useMemo(() => {
    return listings.filter(l => {
      if (search && !l.title.toLowerCase().includes(search.toLowerCase()) && !l.brand?.toLowerCase().includes(search.toLowerCase())) return false;
      if (filters.sizes.length && !filters.sizes.includes(l.size)) return false;
      if (filters.categories.length && !filters.categories.includes(l.category)) return false;
      if (l.sellingPrice > filters.maxPrice) return false;
      if (l.distanceKm > filters.maxDistance) return false;
      return true;
    });
  }, [listings, search, filters]);

  return (
    <div className="min-h-screen pb-20 md:pb-8">
      <AppHeader />

      <div className="max-w-6xl mx-auto px-4">
        {/* Hero banner */}
        <div
          className="mt-6 mb-6 rounded-3xl p-6 md:p-8 overflow-hidden relative"
          style={{
            background: "linear-gradient(135deg, var(--blush) 0%, var(--nude) 60%, var(--gold-light) 100%)",
          }}
        >
          <div className="relative z-10 max-w-lg">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} style={{ color: "var(--rose)" }} />
              <span className="text-xs font-medium" style={{ color: "var(--rose)" }}>LOCAL DISCOVERY</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground leading-tight mb-2">
              Once-worn dresses,<br />
              <span style={{ color: "var(--rose)" }}>loved again.</span>
            </h1>
            <p className="text-sm text-muted-foreground">
              Discover beautiful dresses from women in your city. Each outfit has a story.
            </p>
          </div>
          {/* Decorative elements */}
          <div className="absolute right-6 top-1/2 -translate-y-1/2 hidden md:flex gap-3 opacity-60">
            {["https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=150&q=60",
              "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=150&q=60"].map((src, i) => (
              <div key={i} className={`w-20 h-28 rounded-xl overflow-hidden shadow-md ${i === 1 ? "mt-4" : ""}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search dresses, brands…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 rounded-full border-border/60 bg-card h-10 text-sm"
          />
        </div>

        {/* Filters */}
        <div className="mb-6">
          <FilterBar filters={filters} onChange={setFilters} />
        </div>

        {/* Results header */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            {isLoading ? "Loading…" : `${filtered.length} dresses near you`}
          </p>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden">
                <Skeleton className="aspect-[3/4] w-full" />
                <div className="p-3 space-y-2">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-4xl mb-3">👗</div>
            <h3 className="font-semibold text-lg mb-1">No dresses found</h3>
            <p className="text-sm text-muted-foreground">Try adjusting your filters or search term</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {filtered.map(listing => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
